import type { ClientFrame, HostFrame } from "../protocol/frames";
import { createInProcessTransport } from "./inProcess";
import { createSharedWorkerTransport } from "./sharedWorkerClient";

export interface KernelTransport {
  post: (frame: ClientFrame) => void;
  subscribe: (listener: (frame: HostFrame) => void) => () => void;
  close: () => void;
}

export const HANDSHAKE_TIMEOUT_MS = 3_000;

/** Frames that describe state, so they are worth replaying after a failover. */
const isReplayable = (frame: ClientFrame) =>
  frame.type === "account" ||
  frame.type === "symbols" ||
  frame.type === "visibility";

export interface CreateFailoverTransportOptions {
  createPrimary?: (options: {
    onFatal: () => void;
  }) => KernelTransport | undefined;
  createFallback?: () => KernelTransport;
  handshakeTimeoutMs?: number;
  setTimer?: (handler: () => void, delayMs: number) => () => void;
}

/**
 * Runs on the shared worker, but demotes itself to a local host if the worker
 * never says `ready` or reports an error. Subscribers are unaffected by the
 * switch; the frames that carry state are replayed into the replacement.
 */
export const createFailoverTransport = ({
  createPrimary = createSharedWorkerTransport,
  createFallback = createInProcessTransport,
  handshakeTimeoutMs = HANDSHAKE_TIMEOUT_MS,
  setTimer = (handler, delayMs) => {
    const id = setTimeout(handler, delayMs);
    return () => clearTimeout(id);
  },
}: CreateFailoverTransportOptions = {}): KernelTransport => {
  const listeners = new Set<(frame: HostFrame) => void>();
  // Last write per replayable frame type: `account`, `symbols` and `visibility`
  // are all last-write-wins, and replaying a stale `token` would be worse than
  // replaying nothing — the fallback host asks for its own.
  const retained = new Map<ClientFrame["type"], ClientFrame>();
  let settled = false;
  let closed = false;
  let cancelTimer: (() => void) | undefined;

  const emit = (frame: HostFrame) => {
    for (const listener of [...listeners]) {
      listener(frame);
    }
  };

  const primary = createPrimary({ onFatal: () => demote() });
  let active = primary ?? createFallback();

  const attach = (transport: KernelTransport) =>
    transport.subscribe((frame) => {
      if (frame.type === "ready") {
        settled = true;
        cancelTimer?.();
        cancelTimer = undefined;
        return;
      }
      emit(frame);
    });

  let detach = attach(active);

  function demote() {
    if (settled || closed) {
      return;
    }
    settled = true;
    cancelTimer?.();
    cancelTimer = undefined;
    detach();
    active.close();

    active = createFallback();
    detach = attach(active);
    for (const frame of retained.values()) {
      active.post(frame);
    }
  }

  if (primary === undefined) {
    // No worker to wait on; the fallback is already the active transport. It
    // posted its own `ready` while being constructed, before `attach` could see
    // it, so settle explicitly rather than waiting for a frame that has passed.
    settled = true;
  } else {
    cancelTimer = setTimer(() => {
      console.warn(
        "[atd] shared market data worker never started; using a local socket",
      );
      demote();
    }, handshakeTimeoutMs);
  }

  const post = (frame: ClientFrame) => {
    if (isReplayable(frame)) {
      retained.set(frame.type, frame);
    }
    active.post(frame);
  };

  const subscribe = (listener: (frame: HostFrame) => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const close = () => {
    closed = true;
    cancelTimer?.();
    listeners.clear();
    detach();
    active.close();
  };

  return {
    post,
    subscribe,
    close,
  };
};

/**
 * One socket across windows needs a SharedWorker. Without one — jsdom, or a
 * browser without module SharedWorker support — each window runs its own host,
 * which is exactly today's behavior.
 */
export const createTransport = (): KernelTransport => createFailoverTransport();
